import {
  IAssetV2,
  IMenuDataResponse,
  IMenuItemResponse,
  IMenuSectionResponse,
  IPublicationResponse,
} from 'src/api/getMenu/types';
import { IMenu, IMenuItem, IPublication, ISection } from 'src/types/menu.types';
import { getDayIndex } from 'src/helpers/day/day';
import { IAsset } from 'src/helpers/menus/menus.types';
import { getChannelLabel } from 'src/helpers/channels/getPricingChannels';
import { ItemType } from '@weareopenr/types';

export const findImageUrl = (assets: (IAsset | IAssetV2)[]): string | null => {
  const imageRegex = /\.(png|jpeg|jpg)$/i;
  const assetData = assets?.find((asset: IAsset | IAssetV2) => imageRegex.test(asset.name));

  if (!assetData) return null;

  if ('asset_url' in assetData) return assetData.asset_url; // for IAssetV2

  return assetData?.asset_variants[0]?.url; // for IAsset
};

export const findPDFAsset = (assets: (IAsset | IAssetV2)[]): IAsset | IAssetV2 | null => {
  const pdfRegex = /\.(pdf)$/i;
  return assets?.find((asset: IAsset | IAssetV2) => pdfRegex.test(asset.name)) || null;
};

const distinctByItemId = (menuItems: IMenuItemResponse[]): IMenuItemResponse[] =>
  Array.from(
    menuItems
      .reduce(
        (byId, item) => (byId.has(item.item_id) ? byId : byId.set(item.item_id, item)),
        new Map<string, IMenuItemResponse>(),
      )
      .values(),
  );

const getMenuModifiers = (menuItems: IMenuItemResponse[]) =>
  menuItems.filter((item: IMenuItemResponse) => item.type === ItemType.MODIFIER);

// A section resolves to one card per distinct item it references. An item carries a single
// display_order and so cannot occupy two positions in one section - a repeated reference is one
// item listed twice, not two orderable things. Rendering it twice produced colliding React keys
// and colliding dnd-kit ids, and left the reorder endpoint no way to say which card was meant.
const resolveSectionItems = (references: string[], menuItems: IMenuItemResponse[]): IMenuItemResponse[] => {
  const seen = new Set<string>();

  return references.flatMap((reference: string) => {
    if (seen.has(reference)) return [];
    seen.add(reference);

    const match = menuItems.find(
      (singleItem: IMenuItemResponse) => reference === singleItem.item_id && singleItem.type !== ItemType.MODIFIER,
    );

    return match ? [match] : [];
  });
};

const getMenuSections = (sections: IMenuSectionResponse[], menuItems: IMenuItemResponse[]) =>
  sections.map((section: IMenuSectionResponse) => {
    const extensionInstances = section.availability?.extension_instances;
    const venues = section.availability?.venues;
    return {
      name: section.name,
      sectionId: section.section_id,
      description: section.description,
      displayOrder: section.display_order,
      items: formatMenuItems(resolveSectionItems(section.items, menuItems)),
      parentSectionId: section.parent_section_id,
      instanceIds: extensionInstances ? extensionInstances.map((instance) => instance.extension_instance_id) : null,
      channels: extensionInstances
        ? extensionInstances.map((instance) => ({
            externalSystemId: instance.extension_instance_id,
            name: getChannelLabel({ name: instance.extension_name, extensionInstanceName: instance.name }),
          }))
        : [],
      venueIds: venues ? venues.map((venue) => venue.venue_id) : null,
      venues: venues ? venues.map((venue) => ({ venueId: venue.venue_id, name: venue.name })) : [],
    };
  });

// Maps a menu's sections in isolation - used where the rest of the menu (publication, schedules,
// mealtimes) isn't returned, such as the menus within a menu set.
export const formatMenuSections = (sections: IMenuSectionResponse[], menuItems: IMenuItemResponse[]): ISection[] =>
  getMenuSections(sections, distinctByItemId(menuItems));

const getUncategorised = (sections: ISection[], menuItems: IMenuItemResponse[]) =>
  menuItems.filter(
    (item: IMenuItemResponse) =>
      item.type !== ItemType.MODIFIER &&
      sections.every((section) => section.items.every((sectionItem) => sectionItem.itemId !== item.item_id)),
  );

export const formatMenuItems = (menuItems: IMenuItemResponse[]): IMenuItem[] => {
  return menuItems.map((item: IMenuItemResponse) => ({
    itemId: item.item_id,
    name: item.name,
    consumerName: item.consumer_name,
    type: item.type,
    image: item.asset_url,
    parentItemId: item.parent_item_id,
    displayOrder: item.display_order,
    description: null,
    prices: [],
    tax_rate: null,
    recipeId: null,
    optionGroup: [],
    deposit_return_scheme: null,
  }));
};

export const formatPublication = (publication: IPublicationResponse): IPublication => ({
  publicationStatus: publication.status,
  publicationDate: publication.publication_date ? new Date(publication.publication_date) : null,
  eventId: publication.event_id,
  aggregators: publication.channels.map((aggregator) => ({
    aggregatorId: aggregator.external_system_id,
    publicationStatus: aggregator.status,
    publicationDate: new Date(aggregator.publication_date),
    eventId: aggregator.event_id,
    venues: aggregator.venues.map((venue) => ({
      venueId: venue.venue_id,
      publicationDate: new Date(venue.publication_date),
      publicationStatus: venue.status,
      priceSchemeName: venue.price_scheme_name,
      priceSchemeId: venue.price_scheme_id,
      eventId: venue.event_id,
    })),
  })),
});

export const formatMenu = (data: IMenuDataResponse): IMenu => {
  const menu = {
    name: data.name,
    menuId: data.menu_id,
    consumerName: data.consumer_name,
    image: findImageUrl(data.assets),
    allergensPDF: findPDFAsset(data.assets),
    schedules: data.schedules
      .sort((a, b) => {
        return getDayIndex(a.day_of_week) - getDayIndex(b.day_of_week);
      })
      .map((item) => ({
        dayOfWeek: getDayIndex(item.day_of_week),
        startTime: item.start_time,
        endTime: item.end_time,
      })),
    publication: formatPublication(data.publication),
    created: new Date(data.create_date),
    linkedVenueIds: new Set(data.linked_venues),
    mealtimes: data.mealtimes,
    ranges: data.ranges || [],
    menuLabels: data.menu_labels || [],
    validation: data.validation,
  };

  const items = distinctByItemId(data.items);
  const modifiers = getMenuModifiers(items);
  const sections = getMenuSections(data.sections, items);
  const uncategorised = getUncategorised(sections, items);

  return {
    ...menu,
    modifiers: formatMenuItems(modifiers),
    sections,
    uncategorised: formatMenuItems(uncategorised),
    scheduledPublications: [
      {
        publicationDate: data.scheduled_publication_date ? new Date(data.scheduled_publication_date) : null,
      },
    ],
  };
};
